/**
 * Engine Atlas sub-region inspect shell per Atlas Day 2 visual-layout
 * commitment B.3 + C.1.
 *
 * Renders breadcrumb + sub-region header + 8 vertically-stacked layer
 * sections. Caller passes children (LayerSection + ComposabilityReservedLayer
 * components) per the per-sub-region scope §6 layer content.
 *
 * Per F (navigation/dismissal three-path): breadcrumb click returns to
 * map; ESC key returns; browser back returns. Atlas Day 2.5 P1 wires
 * React Router navigation via useNavigate (replaces Day 2 P6 stateful
 * onReturn callback). All three paths now go through navigate() to
 * /admin/engine-atlas.
 *
 * Max-width: max-w-5xl (same as Atlas title section per Day 1 lock —
 * inspect content is prose-heavy and benefits from the constrained reading
 * width).
 */

import { useEffect, type JSX, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

const ATLAS_ROOT_PATH = '/admin/engine-atlas';

export interface InspectShellProps {
  clusterName?: string;
  subRegionName: string;
  surfaceLine?: string;
  children?: ReactNode;
}

export default function InspectShell({
  clusterName,
  subRegionName,
  surfaceLine,
  children,
}: InspectShellProps): JSX.Element {
  const navigate = useNavigate();
  const returnToMap = (): void => {
    navigate(ATLAS_ROOT_PATH);
  };

  // ESC key returns per F navigation/dismissal three-path
  useEffect(() => {
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') navigate(ATLAS_ROOT_PATH);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [navigate]);

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-5xl mx-auto p-5 space-y-4">
        {/* Breadcrumb per B.3 */}
        <nav
          aria-label="Inspect breadcrumb"
          className="flex items-center gap-1 text-xs text-ink-muted"
        >
          <button
            type="button"
            onClick={returnToMap}
            className="hover:text-ink-secondary transition-colors cursor-pointer"
          >
            Engine Atlas
          </button>
          {clusterName && (
            <>
              <ChevronRight size={12} className="flex-shrink-0" />
              <span>{clusterName}</span>
            </>
          )}
          <ChevronRight size={12} className="flex-shrink-0" />
          <span className="text-ink-secondary">{subRegionName}</span>
        </nav>

        {/* Sub-region header */}
        <div>
          <h1 className="text-xl font-semibold text-ink-bright">{subRegionName}</h1>
          {surfaceLine && <p className="text-sm text-ink-secondary mt-1">{surfaceLine}</p>}
        </div>

        {/* COMPOSABILITY: per-section indicator slot at inspect altitude — wires post-Day 92 */}

        {/* 8 layer sections (caller passes children per scope §6 layer content) */}
        <div className="space-y-4">{children}</div>
      </div>
    </div>
  );
}
